import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import IconButton from '../../atoms/IconButton/IconButton';
import MarkdownContainer from '../../atoms/MarkdownContainer/MarkdownContainer';

// Styles
import styles from './SidePanel.module.css';

/**
 * Animation variants for the panel
 * @type {import('framer-motion').Variants}
 */
const panelVariants = {
  hidden: {
    x: '100%',
  },
  visible: {
    x: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.8, 0.25, 1],
    },
  },
  exit: {
    x: '100%',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.6, 1],
    },
  },
};

/**
 * @param {'batch' | 'form'} size
 * @param {number} stackDepth
 * @returns {string}
 */
function getPanelClassName(size, stackDepth) {
  const sizeClass = size === 'form' ? styles['panel-form'] : styles['panel-batch'];
  const stackClass = stackDepth > 0 ? styles[`panel-stack-${stackDepth}`] : '';
  return [styles.panel, sizeClass, stackClass].filter(Boolean).join(' ');
}

/**
 * SidePanel - Main panel component with optional backdrop
 * @param {object} props
 * @param {boolean} props.isOpen - Whether the panel is open
 * @param {import('react').ReactNode} props.children - Panel content
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.showBackdrop=false] - Whether to show a backdrop behind the panel
 * @param {boolean} [props.backdropBlur=false] - Whether to blur the backdrop
 * @param {() => void} [props.onClose] - Close handler (backdrop click, Escape)
 * @param {string} [props.panelId] - id on the panel root (dialog)
 * @param {'batch' | 'form'} [props.size='batch'] - Panel width variant
 * @param {number} [props.stackDepth=0] - Stack depth for nested panels
 * @param {boolean} [props.usePortal=false] - Portal to document.body
 * @param {() => void} [props.onExitComplete] - Called after the panel slide-out animation ends
 * @returns {JSX.Element | null} SidePanel component
 */
function SidePanel({
  isOpen,
  children,
  className = '',
  showBackdrop = false,
  backdropBlur = false,
  onClose,
  onExitComplete,
  panelId,
  size = 'batch',
  stackDepth = 0,
  usePortal = false,
}) {
  useEffect(() => {
    if (!isOpen || !onClose) return undefined;

    const handleKeyDown = (/** @type {KeyboardEvent} */ e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleBackdropClick = (/** @type {React.MouseEvent} */ e) => {
    if (onClose && e.target === e.currentTarget) {
      onClose();
    }
  };

  const backdropStackClass = stackDepth > 0
    ? styles[`backdrop-stack-${Math.min(stackDepth, 2)}`]
    : '';
  const backdropClassName = [
    styles.backdrop,
    showBackdrop ? styles['backdrop-visible'] : '',
    backdropBlur ? styles['backdrop-blur'] : '',
    size === 'form' ? styles['backdrop-form'] : '',
    backdropStackClass,
  ].filter(Boolean).join(' ');

  const panelClassName = `${getPanelClassName(size, stackDepth)} ${className}`.trim();

  const layerKey = panelId ?? 'side-panel';

  const content = (
    <AnimatePresence onExitComplete={onExitComplete}>
      {isOpen && (
        <>
          {showBackdrop ? (
            <motion.div
              key={`${layerKey}-backdrop`}
              className={backdropClassName}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={handleBackdropClick}
              onKeyDown={undefined}
              role="presentation"
            />
          ) : null}
          <motion.div
            key={`${layerKey}-panel`}
            className={panelClassName}
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            role="dialog"
            aria-modal={showBackdrop ? 'true' : undefined}
            id={panelId}
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  if (usePortal && typeof document !== 'undefined') {
    return createPortal(content, document.body);
  }

  return content;
}

/**
 * SidePanelHeader - Fixed header with title and close button
 * @param {object} props
 * @param {string} [props.title] - Header title
 * @param {number} [props.count] - Number of selected rows
 * @param {() => void} props.onClose - Function to close the panel
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} SidePanelHeader component
 */
function SidePanelHeader({
  title = '', count = 0, onClose, className = '',
}) {
  return (
    <div className={`${styles.header} ${className}`}>
      <h2 className={styles['header-title']} id={title ? 'side-panel-title' : undefined}>
        <MarkdownContainer content={title} as="span" />
        {count > 0 ? (
          <span className={styles['header-count']}>
            {` (${count})`}
          </span>
        ) : null}
      </h2>
      <IconButton
        color="no-background"
        size="medium"
        icon="close-md"
        onClick={onClose}
        className={styles['header-close']}
        title="close"
      />
    </div>
  );
}

/**
 * SidePanelContent - Scrollable content area
 * @param {object} props
 * @param {import('react').ReactNode} props.children - Content
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.id] - Container id for scroll spy
 * @param {'batch' | 'form'} [props.variant='batch'] - Content padding variant
 * @param {boolean} [props.disableScroll=false] - Lock overflow; scroll lives in child columns
 * @returns {JSX.Element} SidePanelContent component
 */
function SidePanelContent({
  children, className = '', id, variant = 'batch', disableScroll = false,
}) {
  const variantClass = variant === 'form' ? styles['content-form'] : styles['content-padded'];
  const liveSearchClass = disableScroll ? styles['content-form-live-search'] : '';
  const contentClassName = [
    styles.content,
    variantClass,
    liveSearchClass,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div id={id} className={contentClassName}>
      {children}
    </div>
  );
}

/**
 * SidePanelActions - Actions container
 * @param {object} props
 * @param {import('react').ReactNode} props.children - Actions content
 * @param {string} [props.className] - Additional CSS classes
 * @param {number} props.selectedRowsCount - Number of selected rows
 * @returns {JSX.Element | null} SidePanelActions component
 */
function SidePanelActions({ children, selectedRowsCount, className = '' }) {
  if (selectedRowsCount <= 1) return null;
  return (
    <div className={`${styles.actions} ${className}`}>
      {children}
    </div>
  );
}

/**
 * SidePanelFooter - Fixed footer with action buttons
 * @param {object} props
 * @param {import('react').ReactNode} props.cancelAction - Cancel button handler
 * @param {import('react').ReactNode} props.applyAction - Apply button handler
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} SidePanelFooter component
 */
function SidePanelFooter({
  cancelAction,
  applyAction,
  className = '',
}) {
  return (
    <div className={`${styles.footer} ${className}`}>
      {cancelAction}
      {applyAction}
    </div>
  );
}

export {
  SidePanel,
  SidePanelHeader,
  SidePanelContent,
  SidePanelFooter,
  SidePanelActions,
};
